import {
  TealiumEventBaseGroup,
  TealiumEventNames,
  TealiumEventCategory,
  EventPageSection,
  ITealiumEvent,
  tealiumEventBaseGroupPrefix,
  IGetEventGroupProps,
  ITealiumEventConstructorProps,
} from '@4tn/core-audio-player-shared';

import { firstInOrder } from '../utils/utility-functions';

import { TealiumEventParent } from './TealiumEventParent';
import { TealiumEventMedia } from './TealiumEventMedia';
import { TealiumEventPage } from './TealiumEventPage';
import { TealiumEventPlayer } from './TealiumEventPlayer';
import { TealiumEventExperiment } from './TealiumEventExperiment';

export class TealiumEvent implements IGetEventGroupProps {
  private event_name: TealiumEventNames;

  private event_category: TealiumEventCategory;

  private event_label?: string | number | null = null;

  private event_value?: string | number | null = null;

  private event_page_section?: EventPageSection | null = null;

  // Event Group references
  private parent: TealiumEventParent;

  private media: TealiumEventMedia;

  private page: TealiumEventPage;

  private player: TealiumEventPlayer;

  private experiment: TealiumEventExperiment;

  constructor({
    eventName,
    eventCategory,
    eventLabel,
    eventValue,
    eventPageSection,
    media,
    page,
    player,
    experiment,
    mediaMetadata,
  }: ITealiumEventConstructorProps) {
    this.event_name = eventName;
    this.event_category = eventCategory;
    if (eventLabel) {
      this.event_label = eventLabel;
    }
    if (eventValue) {
      this.event_value = eventValue;
    }
    if (eventPageSection) {
      this.event_page_section = eventPageSection;
    }

    this.parent = new TealiumEventParent();
    this.media = new TealiumEventMedia(media || {});
    this.page = new TealiumEventPage(page || {});
    this.player = new TealiumEventPlayer(player || {});
    this.experiment = new TealiumEventExperiment(experiment || {});

    if (mediaMetadata) {
      this.media.parseMediaMetadata(mediaMetadata);
      this.player.parseMediaMetadata(mediaMetadata);

      // validate media offset
      this.media.validateMediaOffset(mediaMetadata);
    }
  }

  public getEventGroupProps(): TealiumEventBaseGroup {
    const groupKeys = Object.keys(this).filter((k) => k.startsWith(tealiumEventBaseGroupPrefix));

    // Filter method is returning string array that is ordered by name
    // In order to improve output object readability we need to make sure
    // that the event_name and event_category props are first items in the array
    firstInOrder(groupKeys, 'event_category');
    firstInOrder(groupKeys, 'event_name');

    const eventGroupProps = groupKeys.reduce((o, k) => Object.assign(o, { [k]: this[k] }), {});
    return eventGroupProps as TealiumEventBaseGroup;
  }

  public getEventData(): ITealiumEvent {
    return {
      ...this.getEventGroupProps(),
      ...this.parent?.getEventGroupProps(),
      ...this.media?.getEventGroupProps(),
      ...this.page?.getEventGroupProps(),
      ...this.player?.getEventGroupProps(),
      ...this.experiment?.getEventGroupProps(),
    } as ITealiumEvent;
  }

  public omitEventProps(omitKeys: Array<keyof ITealiumEvent>): Partial<ITealiumEvent> {
    const eventProps = this.getEventData();
    if (omitKeys.length > 0) {
      const filteredKeys = Object.keys(eventProps).filter((k) => (omitKeys as string[]).includes(k));
      const filteredProps = filteredKeys.reduce((o, k) => Object.assign(o, { [k]: this[k] }), {});
      return filteredProps as Partial<ITealiumEvent>;
    }
    return eventProps;
  }

  public static getPageSection(element: HTMLElement): EventPageSection {
    const elementRect = element.getBoundingClientRect();
    const position = window?.pageYOffset || document.documentElement.scrollTop;
    if (elementRect.top < (window?.innerHeight || document.documentElement.clientHeight) + position) {
      return EventPageSection.ABOVE_THE_FOLD;
    }
    return EventPageSection.BELOW_THE_FOLD;
  }
}
